// Mixed payment section — one order settled partly in cash, partly by bank
// transfer. Anything still short of the total is recorded as customer debt, same
// as the single-method sections.
import { useState, useEffect, useRef } from "react";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { MoneyInput } from "@/components/ui/money-input";
import type { PaymentPart } from "./use-pos-cart";

const fmtVND = (n: number) => new Intl.NumberFormat("vi-VN").format(Math.round(n)) + " ₫";

const num = (s: string) => (s.trim() === "" ? 0 : parseFloat(s) || 0);

interface PaymentMixedSectionProps {
  total: number;
  hasCustomer: boolean;
  couponCode?: string;
  onConfirm: (payments: PaymentPart[], couponCode?: string, reason?: string) => void;
  onBack: () => void;
  loading?: boolean;
}

export function PaymentMixedSection({
  total,
  hasCustomer,
  couponCode,
  onConfirm,
  onBack,
  loading,
}: PaymentMixedSectionProps) {
  const [cash, setCash] = useState("");
  const [bank, setBank] = useState("");
  const cashRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setCash("");
    setBank("");
    setTimeout(() => cashRef.current?.focus(), 60);
  }, [total]);

  const cashNum = num(cash);
  const bankNum = num(bank);
  const paid = cashNum + bankNum;
  const shortfall = Math.max(0, total - paid);
  const change = Math.max(0, paid - total);
  const isDebt = shortfall > 0;
  const nothingEntered = paid <= 0;

  // Debt needs a customer to own it. Requiring at least one leg keeps this
  // section honest: "mixed" with nothing entered belongs in the cash section.
  const canConfirm = !loading && !nothingEntered && (!isDebt || hasCustomer);

  const handleConfirm = () => {
    if (!canConfirm) return;
    const payments: PaymentPart[] = [];
    if (cashNum > 0) payments.push({ method: "cash", amount: cashNum });
    if (bankNum > 0) payments.push({ method: "bank_manual", amount: bankNum });
    onConfirm(payments, couponCode, undefined);
  };

  // Fill the other leg with whatever is still owed — the common last step.
  const fillRest = (which: "cash" | "bank") => {
    const other = which === "cash" ? bankNum : cashNum;
    const rest = Math.max(0, Math.round(total - other));
    if (which === "cash") setCash(String(rest));
    else setBank(String(rest));
  };

  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !loading) onBack();
      if (e.key === "Enter" && canConfirm && !loading) handleConfirm();
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, canConfirm, cash, bank]);

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="icon" onClick={onBack} disabled={loading} className="shrink-0">
          <ArrowLeft size={16} />
        </Button>
        <span className="font-semibold text-base">Tiền mặt + Chuyển khoản</span>
      </div>

      <div className="space-y-3 text-sm">
        <div className="flex justify-between">
          <span className="text-muted-foreground">Tổng cộng</span>
          <span className="text-xl font-bold text-primary">{fmtVND(total)}</span>
        </div>

        <div className="flex items-center justify-between gap-3">
          <label className="text-muted-foreground shrink-0">Tiền mặt</label>
          <div className="flex items-center gap-2">
            <MoneyInput
              ref={cashRef}
              value={cash}
              onChange={setCash}
              placeholder="0"
              className="font-mono text-base w-40"
            />
            <Button variant="outline" size="sm" onClick={() => fillRest("cash")} className="text-xs shrink-0">
              Còn lại
            </Button>
          </div>
        </div>

        <div className="flex items-center justify-between gap-3">
          <label className="text-muted-foreground shrink-0">Chuyển khoản</label>
          <div className="flex items-center gap-2">
            <MoneyInput
              value={bank}
              onChange={setBank}
              onKeyDown={(e) => e.key === "Enter" && handleConfirm()}
              placeholder="0"
              className="font-mono text-base w-40"
            />
            <Button variant="outline" size="sm" onClick={() => fillRest("bank")} className="text-xs shrink-0">
              Còn lại
            </Button>
          </div>
        </div>

        <div className="flex justify-between border-t pt-2">
          <span className="text-muted-foreground">Khách đã trả</span>
          <span className="font-mono font-semibold">{fmtVND(paid)}</span>
        </div>

        {isDebt && !nothingEntered && (
          <div
            className={`rounded-md border px-3 py-2 ${
              hasCustomer ? "border-amber-300 bg-amber-50" : "border-destructive/30 bg-destructive/5"
            }`}
          >
            {hasCustomer ? (
              <p className="text-sm text-amber-800 font-medium">
                Khách nợ {fmtVND(shortfall)} (đã trả {fmtVND(paid)})
              </p>
            ) : (
              <p className="text-xs text-destructive">Chọn khách hàng để ghi nợ phần còn thiếu.</p>
            )}
          </div>
        )}

        <div className="flex justify-between border-t pt-3">
          <span className="text-muted-foreground">{isDebt ? "Ghi nợ" : "Tiền thừa"}</span>
          <span className={`text-base font-bold ${isDebt ? "text-amber-600" : "text-green-600"}`}>
            {isDebt ? fmtVND(shortfall) : fmtVND(change)}
          </span>
        </div>
      </div>

      <div className="flex flex-col gap-2 pt-1">
        <Button onClick={handleConfirm} disabled={!canConfirm || loading} className="w-full font-semibold">
          {loading ? "Đang xử lý..." : "Xác nhận (↵)"}
        </Button>
        {nothingEntered && (
          <p className="text-xs text-muted-foreground text-center">
            Nhập ít nhất một ô. Nợ toàn bộ thì dùng "Tiền mặt (F9)".
          </p>
        )}
        <Button variant="outline" onClick={onBack} disabled={loading} className="w-full">
          Quay lại (Esc)
        </Button>
      </div>
    </div>
  );
}
